import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  TablePagination
} from "@mui/material";
import { ChevronsUp, CirclePlus, Search, Upload } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DataTable from "../../components/DataTable";
import {
  getSales,
  updateSale,
  createSale,
  deleteSale,
  bulkUploadSale
} from "../../api/Sale/api";
import { AuthContext } from "../../contexts/AuthContext";
import usePermissions from "./hooks/usePermissions";
import SaleForm from "../../components/shared/forms/SaleForm";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import UploadModal from "../../components/UploadModal";
import { extractErrorMessages } from "../../utils/errorHandler";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const SaleList = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    if (!loadingPermissions && can.viewSale()) {
      fetchSales();
    }
  }, [loadingPermissions]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const response = await getSales({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      if (response?.data) {
        const transformedData = response.data.map((sale) => ({
          id: sale.id,
          duka: sale.duka,
          location: sale.duka_name,
          product: sale.product,
          productName: sale.product_name,
          productGroup: sale.product_group,
          productGroupName: sale.product_group_name,
          code: sale.product_code,
          documentType: sale.document_type,
          amount: sale.amount,
          date: sale.sales_date,
          netPrice: sale.netprice,
          netPriceTotal: sale.netprice_total,
          productVAT: sale.productvat,
          totalSellingPrice: sale.total_selling_price
        }));
        setSales(transformedData);
        setTotal(response.total);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load sales. Please try again later.");
      setSales([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const transformSaleData = (data) => {
    return {
      duka: data.duka,
      product: data.product,
      product_group: data.product_group,
      document_type: data.document_type,
      product_code: data.product_code,
      amount: data.amount,
      sales_date: data.sales_date,
      netprice: data.netprice,
      netprice_total: data.netprice_total,
      productvat: data.productvat,
      total_selling_price: data.total_selling_price
    };
  };

  const handleCreateSale = async (data) => {
    if (!can.createSale()) {
      setError("You do not have permission to add products");
      return;
    }

    try {
      const transformedData = transformSaleData(data);
      await createSale(transformedData);
      showSuccessToast("sale created successfully");
      await fetchSales();
      setOpenModal(false);
      setError(null);
    } catch (err) {
      console.error("Error creating sale:", err);
      const messages = extractErrorMessages(err);
      messages.forEach(message => {
        showErrorToast(message);
        setError(messages);
      });
    }
  };

  const handleUpdateSale = async (id, data) => {
    if (!can.editSale()) {
      setError("You do not have permission to modify products");
      return;
    }

    try {
      const transformedData = transformSaleData(data);
      await updateSale(id, transformedData);
      await fetchSales();
      showSuccessToast("Sale updated successfully");
      setOpenModal(false);
      setSelectedSale(null);
      setError(null);
    } catch (err) {
      console.error("Error updating sale:", err);
      showErrorToast("Failed to update sale. Please try again.");
      setError("Failed to update sale. Please try again.");
    }
  };

  const handleDeleteProduct = async () => {
    if (!can.deleteSale()) {
      setError("You do not have permission to delete products");
      return;
    }

    try {
      await deleteSale(saleToDelete.id);
      await fetchSales();
      setDeleteModalOpen(false);
      setSaleToDelete(null);
      setError(null);
      showSuccessToast("Sale deleted successfully");
    } catch (err) {
      console.error("Error deleting product:", err);
      setError("Failed to delete product. Please try again.");
      showErrorToast("Failed to delete product. Please try again.");
    }
  };

  const filteredSales = sales.filter((sale) =>
    Object.values(sale).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "productGroupName", label: "Product Group" },
    { id: "code", label: "Product Code" },
    { id: "documentType", label: "Document Type" },
    { id: "location", label: "Location" },
    { id: "date", label: "Date" },
    { id: "productName", label: "Product" },
    { id: "amount", label: "Amount" },
    { id: "netPrice", label: "Net Price (KES)" },
    { id: "netPriceTotal", label: "Net Total (KES)"},
    { id: "productVAT", label: "Row VAT (KES)"},
    { id: "totalSellingPrice", label: "Row Total (KES)"},
  ];

  const handleSubmit = (data) => {
    if (selectedSale) {
      handleUpdateSale(selectedSale.id, data);
    } else {
      handleCreateSale(data);
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) {
      showErrorToast("Please select a CSV file", "Validation Error");
      return;
    }
    try {
      await bulkUploadSale(uploadFile);
      showSuccessToast("Bulk upload successful");
      setOpenUploadModal(false);
      fetchSales();
    } catch (err) {
      showErrorToast("Bulk upload failed", "Error");
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Sales
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          {can.createSale() && (
            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => setOpenUploadModal(true)}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Upload CSV
            </Button>
          )}
          {can.createSale() && (
            <Button
              variant="contained"
              startIcon={<CirclePlus size={18} />}
              onClick={() => {
                setSelectedSale(null);
                setOpenModal(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Add Sale
            </Button>
          )}
        </Box>
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredSales}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalRows={total}
        onEdit={
          can.editSale()
            ? (row) => {
                const saleData = {
                  id: row.id,
                  product: row.product,
                  duka: row.duka,
                  product_group: row.productGroup,
                  location: row.location,
                  product_name: row.productName,
                  product_group_name: row.productGroupName,
                  document_type: row.documentType,
                  product_code: row.code,
                  amount: row.amount,
                  sales_date: row.date,
                  netprice: row.netPrice,
                  netprice_total: row.netPriceTotal,
                  productvat: row.productVAT,
                  total_selling_price: row.totalSellingPrice
                };
                setSelectedSale(saleData);
                setOpenModal(true);
              }
            : undefined
        }
        onDelete={
          can.deleteSale()
            ? (row) => {
                const saleData = {
                  id: row.id,
                  name: row.duka_name
                };
                setSaleToDelete(saleData);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        deleteIcon={<DeleteIcon />}
        editTooltip="Edit"
        deleteTooltip="Delete"
        editDisabled={!can.editSale()}
        deleteDisabled={!can.deleteSale()}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ),
        }}
      />

      {(can.createSale() || can.editSale()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedSale(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <SaleForm
              initialData={selectedSale}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedSale(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSaleToDelete(null);
        }}
        onConfirm={handleDeleteProduct}
        title="Delete Sale"
        message="Are you sure you want to delete this sale?"
        itemName={saleToDelete?.name}
      />
      <UploadModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        onFileChange={handleFileChange}
        onUpload={handleCSVUpload}
      />
      <Toast />
    </Box>
  );
};

export default SaleList;
